/**
 * BacklinkApprovalManager — Tinder-style swipe approval for backlink artifacts
 *
 * Workflow:
 *   1. Admin sees one pending article at a time as a card
 *   2. "Read on Claude.ai" button opens the artifact in a new tab
 *   3. Admin returns and swipes right (approve) or left (reject)
 *   4. Approved articles get embedded on their scenario detail page
 */

import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Check, X, ExternalLink, RotateCcw, FileText, Loader2,
  ChevronLeft, ChevronRight, Sparkles, MessageSquareText,
} from "lucide-react";

const T = {
  bg:        "#FAF9F6",
  navy:      "#002868",
  red:       "#B22234",
  green:     "#2b5e49",
  gold:      "#C5A55A",
  text:      "#1B1B1B",
  textMuted: "rgba(27,27,27,0.62)",
  border:    "rgba(27,27,27,0.12)",
  cardBg:    "#FFFFFF",
  shadow:    "0 4px 24px rgba(0,0,0,0.10)",
  shadowLg:  "0 12px 48px rgba(0,0,0,0.15)",
};

type Artifact = {
  id: string;
  scenario_id: string | null;
  artifact_url: string;
  artifact_title: string | null;
  target_url: string;
  platform: string | null;
  approval_status: string;
  reviewed_at: string | null;
  review_note: string | null;
  preview_text: string | null;
  notes: string | null;
  published_at: string | null;
  scenarios?: {
    name: string;
    slug: string;
    category: string | null;
  } | null;
};

type Tab = "queue" | "approved" | "rejected";

export function BacklinkApprovalManager() {
  const [tab, setTab] = useState<Tab>("queue");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [opened, setOpened] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showNote, setShowNote] = useState(false);
  const dragStartX = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: artifacts = [], isLoading } = useQuery({
    queryKey: ["backlink-artifacts", tab],
    queryFn: async () => {
      const status =
        tab === "queue" ? "pending_review" :
        tab === "approved" ? "approved" : "rejected";
      const { data, error } = await (supabase as any)
        .from("backlink_artifacts")
        .select(`
          id, scenario_id, artifact_url, artifact_title, target_url, platform,
          approval_status, reviewed_at, review_note, preview_text, notes, published_at,
          scenarios:scenario_id ( name, slug, category )
        `)
        .eq("approval_status", status)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Artifact[];
    },
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, decision, note }: { id: string; decision: "approved" | "rejected"; note?: string }) => {
      const { error } = await (supabase as any)
        .from("backlink_artifacts")
        .update({
          approval_status: decision,
          reviewed_at: new Date().toISOString(),
          review_note: note || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["backlink-artifacts"] });
      toast({
        title: vars.decision === "approved" ? "Approved" : "Rejected",
        description: vars.decision === "approved"
          ? "Article will appear on scenario page"
          : "Article hidden from scenario page",
      });
      setOpened(false);
      setNoteText("");
      setShowNote(false);
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from("backlink_artifacts")
        .update({
          approval_status: "pending_review",
          reviewed_at: null,
          review_note: null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backlink-artifacts"] });
      toast({ title: "Moved back to queue" });
    },
  });

  // Reset index when switching tabs or after a decision
  useEffect(() => {
    setCurrentIdx(0);
    setDragX(0);
    setOpened(false);
  }, [tab, artifacts.length]);

  const current = artifacts[0]; // Always show the first pending one (after each decision, list shrinks)

  const handlePointerDown = (e: React.PointerEvent) => {
    if (tab !== "queue") return;
    dragStartX.current = e.clientX;
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setDragX(e.clientX - dragStartX.current);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = 120;
    if (dragX > threshold) {
      // Swipe right = approve
      handleDecision("approved");
    } else if (dragX < -threshold) {
      // Swipe left = reject
      handleDecision("rejected");
    } else {
      setDragX(0);
    }
  };

  const handleDecision = (decision: "approved" | "rejected") => {
    if (!current) return;
    // Animate off-screen
    setDragX(decision === "approved" ? 800 : -800);
    setTimeout(() => {
      reviewMutation.mutate({ id: current.id, decision, note: noteText });
      setDragX(0);
    }, 200);
  };

  const handleOpenArtifact = () => {
    if (!current) return;
    window.open(current.artifact_url, "_blank", "noopener,noreferrer");
    setOpened(true);
  };

  // ─────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Sub-tab nav */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}` }}>
        {([
          { id: "queue", label: "Pending Review", icon: <FileText size={14} /> },
          { id: "approved", label: "Approved", icon: <Check size={14} /> },
          { id: "rejected", label: "Rejected", icon: <X size={14} /> },
        ] as { id: Tab; label: string; icon: React.ReactNode }[]).map(t => {
          const active = tab === t.id;
          const count = (tab === t.id ? artifacts.length : null);
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 22px",
                border: "none",
                borderBottom: active ? `3px solid ${T.red}` : "3px solid transparent",
                backgroundColor: active ? "rgba(178,34,52,0.04)" : "transparent",
                color: active ? T.navy : T.textMuted,
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
                marginBottom: -1,
              }}
            >
              {t.icon}
              {t.label}
              {count !== null && count > 0 && (
                <span style={{
                  backgroundColor: active ? T.red : T.textMuted,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  borderRadius: 999,
                  padding: "1px 8px",
                  minWidth: 20,
                  textAlign: "center",
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {isLoading && (
        <div style={{ padding: 60, textAlign: "center", color: T.textMuted }}>
          <Loader2 size={28} className="animate-spin" style={{ marginBottom: 8 }} />
          <div>Loading articles...</div>
        </div>
      )}

      {!isLoading && tab === "queue" && (
        <>
          {artifacts.length === 0 ? (
            <EmptyState
              icon={<Sparkles size={36} color={T.gold} />}
              title="All caught up!"
              text="No articles pending review. Approved articles appear on scenario pages."
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }}>
              {/* Progress */}
              <div style={{ fontSize: 13, color: T.textMuted, fontWeight: 600, letterSpacing: "0.04em" }}>
                {artifacts.length} ARTICLE{artifacts.length === 1 ? "" : "S"} REMAINING
              </div>

              {/* Swipe card */}
              <div style={{ position: "relative", width: "100%", maxWidth: 640, height: 520 }}>
                {/* Background card preview (next item) */}
                {artifacts[1] && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: T.cardBg,
                      borderRadius: 16,
                      border: `1px solid ${T.border}`,
                      transform: "scale(0.96) translateY(8px)",
                      opacity: 0.6,
                      boxShadow: T.shadow,
                    }}
                  />
                )}

                {/* Active card */}
                {current && (
                  <div
                    ref={cardRef}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: T.cardBg,
                      borderRadius: 16,
                      border: `1px solid ${T.border}`,
                      boxShadow: T.shadowLg,
                      cursor: isDragging ? "grabbing" : "grab",
                      transform: `translateX(${dragX}px) rotate(${dragX * 0.04}deg)`,
                      transition: isDragging ? "none" : "transform 0.3s ease",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      userSelect: "none",
                      touchAction: "none",
                    }}
                  >
                    {/* Decision indicators (during drag) */}
                    {dragX > 30 && (
                      <div style={{
                        position: "absolute",
                        top: 24,
                        left: 24,
                        padding: "8px 16px",
                        border: `4px solid ${T.green}`,
                        borderRadius: 8,
                        color: T.green,
                        fontSize: 24,
                        fontWeight: 900,
                        letterSpacing: "0.1em",
                        transform: "rotate(-12deg)",
                        opacity: Math.min(dragX / 120, 1),
                        zIndex: 5,
                      }}>APPROVE</div>
                    )}
                    {dragX < -30 && (
                      <div style={{
                        position: "absolute",
                        top: 24,
                        right: 24,
                        padding: "8px 16px",
                        border: `4px solid ${T.red}`,
                        borderRadius: 8,
                        color: T.red,
                        fontSize: 24,
                        fontWeight: 900,
                        letterSpacing: "0.1em",
                        transform: "rotate(12deg)",
                        opacity: Math.min(-dragX / 120, 1),
                        zIndex: 5,
                      }}>REJECT</div>
                    )}

                    {/* Header band */}
                    <div style={{
                      backgroundColor: T.navy,
                      padding: "16px 24px",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: "50%",
                          backgroundColor: T.gold,
                        }} />
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                          Claude.ai Article
                        </span>
                      </div>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        {current.published_at ? new Date(current.published_at).toLocaleDateString() : ""}
                      </span>
                    </div>

                    {/* Body */}
                    <div style={{ flex: 1, padding: "24px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
                      {/* Scenario context */}
                      {current.scenarios && (
                        <div style={{
                          display: "inline-flex",
                          alignSelf: "flex-start",
                          alignItems: "center",
                          gap: 6,
                          backgroundColor: "rgba(0,40,104,0.06)",
                          color: T.navy,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "5px 11px",
                          borderRadius: 999,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                        }}>
                          {current.scenarios.category || "Scenario"}
                        </div>
                      )}

                      {/* Title */}
                      <h3 style={{
                        fontFamily: "'Instrument Serif', Georgia, serif",
                        fontSize: 26,
                        lineHeight: 1.25,
                        color: T.text,
                        margin: 0,
                        fontWeight: 400,
                      }}>
                        {current.artifact_title || "Untitled Article"}
                      </h3>

                      {/* Linked scenario */}
                      {current.scenarios && (
                        <div style={{ fontSize: 13, color: T.textMuted }}>
                          Links to: <span style={{ color: T.navy, fontWeight: 600 }}>{current.scenarios.name}</span>
                        </div>
                      )}

                      {/* Old notes (if any) */}
                      {current.notes && (
                        <div style={{
                          padding: 12,
                          backgroundColor: "rgba(178,34,52,0.05)",
                          border: `1px solid rgba(178,34,52,0.2)`,
                          borderRadius: 6,
                          fontSize: 12,
                          color: T.textMuted,
                          fontStyle: "italic",
                        }}>
                          📝 Prior note: {current.notes}
                        </div>
                      )}

                      <div style={{ flex: 1 }} />

                      {/* Read button */}
                      <button
                        onClick={handleOpenArtifact}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          padding: "14px 24px",
                          backgroundColor: opened ? "rgba(43,94,73,0.1)" : T.navy,
                          color: opened ? T.green : "#fff",
                          border: opened ? `1px solid ${T.green}` : "none",
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 700,
                          cursor: "pointer",
                          letterSpacing: "0.02em",
                        }}
                      >
                        {opened ? <Check size={16} /> : <ExternalLink size={16} />}
                        {opened ? "Opened — Read & Decide" : "Read on Claude.ai"}
                      </button>

                      {/* Optional note */}
                      {showNote ? (
                        <textarea
                          value={noteText}
                          onChange={e => setNoteText(e.target.value)}
                          placeholder="Optional note (e.g. why rejecting)..."
                          rows={2}
                          style={{
                            border: `1px solid ${T.border}`,
                            borderRadius: 6,
                            padding: 8,
                            fontSize: 12,
                            fontFamily: "inherit",
                            resize: "none",
                          }}
                        />
                      ) : (
                        <button
                          onClick={() => setShowNote(true)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            background: "none",
                            border: "none",
                            color: T.textMuted,
                            fontSize: 11,
                            cursor: "pointer",
                            padding: 0,
                            alignSelf: "flex-start",
                          }}
                        >
                          <MessageSquareText size={12} />
                          Add a note
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                <ActionButton
                  onClick={() => handleDecision("rejected")}
                  bg={T.red}
                  icon={<X size={28} />}
                  label="Reject"
                />
                <div style={{ fontSize: 11, color: T.textMuted, textAlign: "center", maxWidth: 140 }}>
                  Or drag the card<br />← left or right →
                </div>
                <ActionButton
                  onClick={() => handleDecision("approved")}
                  bg={T.green}
                  icon={<Check size={28} />}
                  label="Approve"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Approved / Rejected list views */}
      {!isLoading && tab !== "queue" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {artifacts.length === 0 ? (
            <EmptyState
              icon={tab === "approved" ? <Check size={36} color={T.green} /> : <X size={36} color={T.red} />}
              title={tab === "approved" ? "No approved articles yet" : "No rejected articles"}
              text={tab === "approved" ? "Approve articles in the queue to see them here" : "Rejected articles will appear here"}
            />
          ) : (
            artifacts.map(a => (
              <div key={a.id} style={{
                backgroundColor: T.cardBg,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: 16,
                display: "flex",
                gap: 16,
                alignItems: "flex-start",
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                    {a.scenarios?.category && (
                      <span style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: T.navy,
                        backgroundColor: "rgba(0,40,104,0.06)",
                        padding: "2px 8px",
                        borderRadius: 999,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}>{a.scenarios.category}</span>
                    )}
                    <span style={{ fontSize: 11, color: T.textMuted }}>
                      {a.reviewed_at ? `Reviewed ${new Date(a.reviewed_at).toLocaleDateString()}` : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                    {a.artifact_title || "Untitled"}
                  </div>
                  {a.scenarios && (
                    <div style={{ fontSize: 12, color: T.textMuted }}>
                      → {a.scenarios.name}
                    </div>
                  )}
                  {a.review_note && (
                    <div style={{ marginTop: 8, fontSize: 12, fontStyle: "italic", color: T.textMuted }}>
                      Note: {a.review_note}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <a
                    href={a.artifact_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "6px 10px",
                      border: `1px solid ${T.border}`,
                      borderRadius: 6,
                      color: T.navy,
                      fontSize: 11,
                      fontWeight: 600,
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <ExternalLink size={11} />
                    View
                  </a>
                  <button
                    onClick={() => restoreMutation.mutate(a.id)}
                    style={{
                      padding: "6px 10px",
                      border: `1px solid ${T.border}`,
                      borderRadius: 6,
                      backgroundColor: "transparent",
                      color: T.textMuted,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <RotateCcw size={11} />
                    Re-review
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ActionButton({ onClick, bg, icon, label }: { onClick: () => void; bg: string; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      title={label}
      style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        border: `3px solid ${bg}`,
        backgroundColor: "#fff",
        color: bg,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: T.shadow,
        transition: "all 0.18s ease",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = bg;
        e.currentTarget.style.color = "#fff";
        e.currentTarget.style.transform = "scale(1.08)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = "#fff";
        e.currentTarget.style.color = bg;
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      {icon}
    </button>
  );
}

function EmptyState({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div style={{
      padding: 80,
      textAlign: "center",
      backgroundColor: T.cardBg,
      borderRadius: 12,
      border: `1px dashed ${T.border}`,
    }}>
      <div style={{ display: "inline-flex", marginBottom: 16 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: T.textMuted }}>{text}</div>
    </div>
  );
}
